import React, { Fragment, useContext } from 'react';
import moment from 'moment-timezone';
import { HubEventsCarouselProps, HubEventsItemProps } from '../type';
import LocalizationContext from '../../../contexts/Localizations';
import { Link, SvgImage } from '../../common';
import Carousel from '../../Carousel';
import './hubEvents.scss';

const HubEventItem = ({
  index, event, timezone, sectionTitle,
}: HubEventsItemProps) => {
  const { translations, locale } = useContext(LocalizationContext);
  const disciplineClass = event?.discipline?.toLowerCase().split(' ').join('-');
  const momentTz = timezone === 'BST' || timezone === 'GMT' ? 'GMT' : timezone;

  const isLive = sectionTitle === 'live';
  const isUpcoming = sectionTitle === 'upcoming';
  const liveStream = moment().tz(momentTz!).isBefore(event?.liveStreamEndTime)
    && event.liveStreamStartTime;

  const isFrench = locale === 'fr-fr';
  const raceName = isFrench ? event?.racePicker?.nameFra : event.racePicker?.nameEng;
  const eventCategoryName = isFrench ? event?.racePicker?.events[0]?.nameFra
    : event?.racePicker?.events[0]?.nameEng;

  return (
    <div className="event-hub-carousel__item">
      {!index && (
        <div className={`event-hub-carousel__status ${isLive ? 'is-live' : ''}`}>
          {isLive && translations.event.liveNowLabel}
          {isUpcoming && translations.event.upcomingLabel}
        </div>
      )}
      <div className={`event-hub-carousel__item__content ${disciplineClass}`}>
        {isLive ? (
          <div className="event-hub-carousel__item__time is-live">
            {translations.homepage.liveEventLabel}
          </div>
        ) : (
          <div className="event-hub-carousel__item__time">
            {event.raceStartTime.slice(11, 16)}
            <span className="event-hub-carousel__item__timezone">{timezone}</span>
          </div>
        )}

        <div className="event-hub-carousel__item__details">
          <div className="event-hub-carousel__item__discipline">
            {event.discipline}
          </div>
          <div className="event-hub-carousel__item__race-info">
            {raceName || event.title}
            {eventCategoryName && (
              <span>
                {' - '}
                {eventCategoryName}
              </span>
            )}
          </div>

          {Boolean(liveStream) && (
            <div className="event-hub-carousel__item__livestream">
              <SvgImage title="Play video" reference="#icon-video" />
              {translations.event.liveStreamLabel}
              <span>
                {translations.event.fromLabel}
                {' '}
                {event.liveStreamStartTime.slice(11, 16)}
              </span>
            </div>
          )}

          {event.raceHub && (
            <Link
              {...event.raceHub}
              title={translations.calendar.raceHubLinkLabel}
              iconAfter={{
                title: 'arrow-right',
                reference: '#icon-think-arrow',
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

const HubEventsCarousel = ({
  pastEvents,
  liveEvents,
  upcomingEvents,
  timezone,
  carouselTitle,
  liveOrUpcomingEventsCount,
}: HubEventsCarouselProps) => {
  const getSlidesToShow = (idealSlidesToShowValue: number) => (
    idealSlidesToShowValue > liveOrUpcomingEventsCount
      ? liveOrUpcomingEventsCount
      : idealSlidesToShowValue
  );

  return (
    <div className="event-hub-carousel">
      <Carousel
        title={carouselTitle}
        slickConfig={{
          variableWidth: true,
          slidesToShow: getSlidesToShow(5),
          slidesToScroll: 1,
          initialSlide: liveOrUpcomingEventsCount ? pastEvents?.length : 0,
          responsive: [
            {
              breakpoint: 2000,
              settings: {
                slidesToShow: getSlidesToShow(4),
              },
            },
            {
              breakpoint: 1600,
              settings: {
                slidesToShow: getSlidesToShow(3),
              },
            },
            {
              breakpoint: 1150,
              settings: {
                slidesToShow: getSlidesToShow(2),
              },
            },
            {
              breakpoint: 750,
              settings: {
                slidesToShow: 1,
                variableWidth: true,
              },
            },
            {
              breakpoint: 450,
              settings: {
                slidesToShow: 1,
                variableWidth: false,
              },
            },
          ],
        }}
      >

        {pastEvents?.map((event, index) => (
          <Fragment key={event.title}>
            <HubEventItem event={event} index={index} timezone={timezone} sectionTitle="past" />
          </Fragment>
        ))}
        {liveEvents?.map((event, index) => (
          <Fragment key={event.title}>
            <HubEventItem event={event} index={index} timezone={timezone} sectionTitle="live" />
          </Fragment>
        ))}
        {upcomingEvents?.map((event, index) => (
          <Fragment key={event.title}>
            <HubEventItem event={event} index={index} timezone={timezone} sectionTitle="upcoming" />
          </Fragment>
        ))}
      </Carousel>
    </div>
  );
};

export default HubEventsCarousel;
